import { memo, useRef, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy, DownloadSimple } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/**
 * The agent ends every tool-backed answer with a "How I got this" section
 * (spec ids, SQL). It is for whoever wants to check the work, not for the
 * answer itself, so it renders collapsed. Matches the heading on its own line
 * in any of the forms the model uses: "How I got this", "**How I got this**",
 * "### How I got this", with or without a trailing colon.
 */
const HOW_I_GOT_THIS =
  /^(?:#{1,6}\s*)?(?:\*\*)?How I got this(?:\*\*)?:?(?:\*\*)?\s*$/im;

export function splitProvenance(content: string): {
  answer: string;
  provenance: string | null;
} {
  const m = HOW_I_GOT_THIS.exec(content);
  if (!m) return { answer: content, provenance: null };
  const provenance = content.slice(m.index + m[0].length).trim();
  // Nothing after the heading yet (still streaming, or the model stopped
  // there): leave the text untouched rather than cutting the heading away.
  if (provenance.length === 0) return { answer: content, provenance: null };
  return {
    answer: content.slice(0, m.index).replace(/\s+$/, ''),
    provenance,
  };
}

/**
 * Renders assistant markdown with the app's design tokens: GFM tables get a
 * scrollable card treatment with copy-as-CSV/Markdown affordances, code render
 * in a mono block with copy, and typography stays compact and readable.
 */
export const MarkdownMessage = memo(function MarkdownMessage({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const { answer, provenance } = splitProvenance(content);
  return (
    <div className={cn('min-w-0 space-y-3 text-sm leading-relaxed', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {answer}
      </ReactMarkdown>
      {provenance && (
        <details className="rounded-md border border-border/60 bg-muted/30 text-xs">
          <summary className="cursor-pointer select-none px-2.5 py-1.5 font-medium text-muted-foreground hover:text-foreground">
            How I got this
          </summary>
          <div className="space-y-2 border-t border-border/60 px-2.5 py-2 text-muted-foreground [&_pre]:text-[11px]">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
              {provenance}
            </ReactMarkdown>
          </div>
        </details>
      )}
    </div>
  );
});

function extractTableMatrix(table: HTMLTableElement): string[][] {
  return Array.from(table.querySelectorAll('tr')).map((row) =>
    Array.from(row.querySelectorAll('th,td')).map((cell) =>
      (cell.textContent ?? '').trim(),
    ),
  );
}

function matrixToCsv(matrix: string[][]): string {
  return matrix
    .map((row) =>
      row
        .map((cell) =>
          /[",\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell,
        )
        .join(','),
    )
    .join('\n');
}

function TableBlock({ children }: { children?: React.ReactNode }) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState<'csv' | 'rows' | null>(null);

  const copy = async (kind: 'csv' | 'rows') => {
    const table = wrapperRef.current?.querySelector('table');
    if (!table) return;
    const matrix = extractTableMatrix(table);
    const text =
      kind === 'csv'
        ? matrixToCsv(matrix)
        : matrix.map((row) => row.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="group/table relative" ref={wrapperRef}>
      <div className="absolute -top-2.5 right-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover/table:opacity-100">
        <button
          type="button"
          onClick={() => copy('csv')}
          className="flex items-center gap-1 rounded-md border bg-card px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm hover:text-foreground"
          title="Copy as CSV"
        >
          {copied === 'csv' ? (
            <Check className="h-3 w-3 text-success" />
          ) : (
            <DownloadSimple className="h-3 w-3" />
          )}
          CSV
        </button>
        <button
          type="button"
          onClick={() => copy('rows')}
          className="flex items-center gap-1 rounded-md border bg-card px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm hover:text-foreground"
          title="Copy rows (tab-separated)"
        >
          {copied === 'rows' ? (
            <Check className="h-3 w-3 text-success" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          Rows
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse text-xs [font-variant-numeric:tabular-nums]">
          {children}
        </table>
      </div>
    </div>
  );
}

function CodeBlock({ children }: { children?: React.ReactNode }) {
  const preRef = useRef<HTMLPreElement>(null);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = preRef.current?.textContent ?? '';
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <div className="group/code relative">
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 z-10 rounded-md border bg-card p-1 text-muted-foreground opacity-0 shadow-sm transition-opacity hover:text-foreground group-hover/code:opacity-100"
        title="Copy code"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-success" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
      <pre
        ref={preRef}
        className="overflow-x-auto rounded-lg border bg-muted/50 p-3 font-mono text-xs leading-relaxed"
      >
        {children}
      </pre>
    </div>
  );
}

const components: Components = {
  table: ({ children }) => <TableBlock>{children}</TableBlock>,
  thead: ({ children }) => (
    <thead className="bg-muted/60 text-muted-foreground">{children}</thead>
  ),
  th: ({ children, style }) => (
    <th
      style={style}
      className="whitespace-nowrap border-b px-2.5 py-1.5 text-left font-semibold"
    >
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="border-b px-2.5 py-1.5 align-top">
      {children}
    </td>
  ),
  tr: ({ children }) => (
    <tr className="last:[&>td]:border-b-0 odd:bg-transparent even:bg-muted/25">
      {children}
    </tr>
  ),
  pre: ({ children }) => <CodeBlock>{children}</CodeBlock>,
  code: ({ children, className }) => (
    <code
      className={cn(
        'rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]',
        // Inside a <pre> the block styles own the presentation.
        className && 'bg-transparent p-0',
        className,
      )}
    >
      {children}
    </code>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-primary underline underline-offset-2 hover:opacity-80"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => (
    <ul className="ml-5 list-disc space-y-1 [&_ul]:mt-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="ml-5 list-decimal space-y-1">{children}</ol>
  ),
  h1: ({ children }) => (
    <h1 className="mt-1 text-base font-semibold">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="mt-1 text-base font-semibold">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mt-1 text-sm font-semibold">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mt-1 text-sm font-semibold">{children}</h4>
  ),
  p: ({ children }) => <p>{children}</p>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-primary/40 pl-3 text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-border" />,
};
